import { Select, Tooltip } from 'components';
import { format } from 'date-fns';
import { cn } from 'domain/core';
import { useAlertPreview } from 'domain/hooks/api/alerts';
import { useRelEntities } from 'domain/hooks/hal/hal.service';
import { useConvertData } from 'domain/hooks/useConvertData';
import { useDebounce } from 'domain/hooks/useDebounce';
import { AlertDefinition, IdLabel } from 'domain/models/api.models';
import { KeyValue } from 'domain/models/utils.model';
import { decimals } from 'domain/utils';
import { DATE_TIME_S_FORMAT } from 'domain/utils/time.utils';
import { AlertList } from 'pages/common/AlertList';
import {
	useAxisConfiguration,
	useCurrentVars,
	useLegendProps,
} from 'pages/observations/ObservationGraphics';
import { AxisConfigurationType } from 'pages/observations/types';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	ResponsiveContainer,
	ComposedChart,
	CartesianGrid,
	XAxis,
	YAxis,
	Legend,
	Line,
	Bar,
	ReferenceArea,
	Area,
} from 'recharts';

export const AlertPreview = ({ values }: { values: AlertDefinition }) => {
	const { t } = useTranslation(['tutorial']);

	const debounced = useDebounce(values, 300);

	const availableAxisConfiguration = useAxisConfiguration();

	const [selectedVs, setSelectedVs] = useState<string | number | undefined>();

	useEffect(() => {
		if (!selectedVs) {
			setSelectedVs(debounced.virtualStations?.[0]?.id ?? undefined);
		}
	}, [debounced]);

	const {
		stationData,
		alertData,
		loading: dataLoading,
	} = useAlertPreview(debounced, {
		virtualStation: selectedVs,
	});

	const [data, availableVariables] = useConvertData(stationData);

	const axisConfiguration = useMemo(() => {
		const axis: AxisConfigurationType = {};
		for (const key of Object.keys(availableAxisConfiguration)) {
			if (availableVariables.has(key)) {
				axis[key] = availableAxisConfiguration[key];
			}
		}
		return axis;
	}, [availableVariables]);

	const loadingClass = useMemo(
		() => (dataLoading ? 'animate-[pulse_0.5s_ease-in-out_infinite]' : ''),
		[dataLoading]
	);

	const selected = useMemo(() => {
		return debounced.virtualStations?.find((vs) => vs.id === selectedVs);
	}, [debounced.virtualStations, selectedVs]);

	const [virtualStations] = useRelEntities('virtual-stations');

	const currentVars = useCurrentVars(data);
	const legendProps = useLegendProps(currentVars);

	const [currentSelected, setCurrentSelected] = useState<KeyValue<string>[]>([
		{},
	]);

	const stationsToPreview: IdLabel[] = useMemo(() => {
		if (!debounced?.virtualStations?.length) {
			// All stations
			if (!virtualStations) {
				return [];
			}
			return virtualStations.map((vs: any) => ({
				id: vs.id,
				label: vs.name,
			}));
		}
		return debounced?.virtualStations ?? ([] as IdLabel[]);
	}, [debounced?.virtualStations, virtualStations]);

	const onMouseMove = (e: any) => {
		if (!e?.activePayload?.length) {
			return;
		}
		const { activePayload } = e;
		const payload = activePayload[0].payload;
		let currentSelected: KeyValue<string>[] = [];
		let time;
		for (const varKey of Object.keys(payload)) {
			if (!payload[varKey]) {
				continue;
			}

			if (varKey === 'ts') {
				time = new Date(payload[varKey]);
				continue;
			}

			const key = t(`variables:abbr.${varKey}`);
			const title = t(`variables:${varKey}`);
			const value = decimals(payload[varKey], 1);
			const unit = t(`variables:unit.${varKey}`);
			currentSelected.push({
				key: key,
				title: title,
				value: `${value}${unit}`,
			});
		}
		if (time) {
			currentSelected = [
				{
					key: format(time, DATE_TIME_S_FORMAT),
					value: '',
				},
				...currentSelected,
			];
		}
		setCurrentSelected(currentSelected);
	};

	return (
		<div className={cn(loadingClass)}>
			{/* Select the crop, as different virtualStations have different stations */}
			<div className='flex w-full flex-row justify-end'>
				<div className='align-center flex w-64 flex-row justify-center py-4'>
					<div>
						<Select
							className='w-40 text-sm'
							value={selected}
							onChange={(v: any) => {
								setSelectedVs(v.value);
							}}
							options={stationsToPreview?.map((vs) => {
								return {
									label: vs.label,
									value: vs.id,
								};
							})}
						/>
					</div>
				</div>
			</div>
			<div className='flex w-full py-2 font-mono text-sm'>
				{currentSelected.map((data) => {
					return (
						<div key={data.key} className='min-w-[100px] pr-4'>
							<Tooltip content={data.title ?? ''}>
								<span className='font-bold'>{data.key}</span>
							</Tooltip>{' '}
							{data.value}
						</div>
					);
				})}
			</div>
			<ResponsiveContainer width='100%' height={300}>
				<ComposedChart
					onMouseMove={onMouseMove}
					width={500}
					height={300}
					data={data}
					margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
					<defs>
						<linearGradient id={`color#de2d26`} x1='0' y1='0' x2='0' y2='1'>
							<stop offset='0%' stopColor='#de2d26' stopOpacity={0.7}></stop>
							<stop offset='75%' stopColor='#de2d26' stopOpacity={0.5}></stop>
						</linearGradient>
						<linearGradient id={`color#756bb1`} x1='0' y1='0' x2='0' y2='1'>
							<stop offset='0%' stopColor='#756bb1' stopOpacity={0.7}></stop>
							<stop offset='75%' stopColor='#756bb1' stopOpacity={0.5}></stop>
						</linearGradient>
						<linearGradient id={`color#DE7F31`} x1='0' y1='0' x2='0' y2='1'>
							<stop offset='0%' stopColor='#DE7F31' stopOpacity={0.7}></stop>
							<stop offset='75%' stopColor='#DE7F31' stopOpacity={0.5}></stop>
						</linearGradient>
					</defs>
					<CartesianGrid strokeDasharray='3 3' />
					<XAxis
						scale='time'
						dataKey='ts'
						style={{ fontSize: '12px' }}
						tickFormatter={(timeStr) =>
							format(new Date(timeStr), DATE_TIME_S_FORMAT)
						}
					/>
					{Object.entries(axisConfiguration).map(([key, axis]) => {
						if (axis.hidden) {
							return <YAxis key={`yaxis-${key}`} yAxisId={key} hide={true} />;
						}
						return (
							<YAxis
								key={`yaxis-${key}`}
								style={{ fontSize: '12px' }}
								dx={0}
								yAxisId={key}
								label={{
									value: `${axis.label} / ${axis.unit}`,
									angle: -90,
									fontSize: 12,
								}}
								orientation={axis.axis === 'right' ? 'right' : 'left'}
								tickFormatter={(value) => (value ? value.toFixed(0) : '-')}
								domain={axis.domain}
							/>
						);
					})}
					{Object.entries(axisConfiguration).map(([key, axis]) => {
						if (axis.type === 'bar') {
							return (
								<Bar key={key} yAxisId={key} dataKey={key} fill={axis.color} />
							);
						}

						if (axis.type === 'area') {
							return (
								<Area
									key={key}
									isAnimationActive={false}
									dot={false}
									yAxisId={key}
									dataKey={key}
									stroke={axis.color}
									strokeWidth={1.5}
									fill={axis.fill}
								/>
							);
						}
						return (
							<Line
								key={key}
								isAnimationActive={false}
								orientation={axis.axis === 'right' ? 'right' : 'left'}
								dot={false}
								yAxisId={key}
								dataKey={key}
								stroke={axis.color}
								strokeWidth={1.5}
							/>
						);
					})}

					<Legend {...legendProps} />
					{alertData.map((alert, index) => {
						return (
							<ReferenceArea
								key={index}
								yAxisId='tas'
								x={new Date(alert.alertTime).getTime()}
								x1={new Date(alert.alertTime).getTime()}
								x2={new Date(alert.endTime).getTime()}
								fill={'#FF0000bb'}
							/>
						);
					})}
				</ComposedChart>
			</ResponsiveContainer>
			<AlertList list={alertData} />
			{/* Explanation of the graphics */}
			<div className='flex flex-col justify-between pb-2 text-sm'>
				<p className='py-2'>{t('tutorial:alerts.definition.preview.line_1')}</p>
				<p className='py-2'>{t('tutorial:alerts.definition.preview.line_2')}</p>
				<p className='py-2'>{t('tutorial:alerts.definition.preview.line_3')}</p>
				<p className='py-2'>{t('tutorial:alerts.definition.preview.line_4')}</p>
			</div>
		</div>
	);
};
